import React from 'react'
import GradientAttribute from '../GradientAttribute'
import Image from '../Attribute/components/Image/Image'
import Attribute, { Label, AttributeList } from '../Attribute'
import Header, { SubTitle } from '../Header'
import FullCopyAttribute from '../FullCopyAttribute'
import {
  ColorFormat,
  Fill,
  FillType,
  LayerType,
  ColorVariable,
  Color as ColorType,
  Gradient,
} from 'modules/shares/types'
import ColorAttribute from '../ColorAttribute'
import copy from './copy'
import { capitalize } from 'utils'
import { getDirtyAttributes } from 'modules/shares/Inspector/Sidebar/components/Style/DirtyIconTooltip'

export interface FillsProps {
  fills: Fill[]
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  layerType?: LayerType
  colorVariables: { [key: string]: ColorVariable }
  originalValues?: Fill[]
}

function getDirtyHeaderAttributes(originalValues?: Fill[]) {
  if (!originalValues) {
    return undefined
  }

  // this indicates that previously the layer style did NOT have fills, but now it does
  if (
    originalValues[0] === null ||
    Object.values(originalValues[0]).length === 0
  ) {
    return { originalValue: 'disabled', originalProperty: 'Fill value' }
  }

  return undefined
}

const isBlendModeVisible = (blendMode: string) =>
  blendMode.toUpperCase() !== 'NORMAL'

const renderFill = (
  {
    isEnabled,
    type,
    color,
    gradient,
    patternFillType,
    patternTileScale,
    appearance,
  }: Fill,
  {
    onColorFormatChange,
    colorFormat,
    colorVariables,
  }: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
    colorVariables: { [key: string]: ColorVariable }
  },
  i: number,
  originalValue?: Fill
) => {
  if (!isEnabled) {
    return null
  }

  const blendMode = appearance?.blendMode
  const opacity = appearance?.opacity ?? 1

  let renderedType = null
  switch (type) {
    case FillType.Color: {
      renderedType = color ? (
        <ColorAttribute
          red={color.red}
          green={color.green}
          blue={color.blue}
          alpha={color.alpha}
          colorVariableID={color.colorVariableID}
          colorVariables={colorVariables}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          dirtyAttributes={
            originalValue?.color
              ? {
                  originalValue: originalValue?.color as ColorType,
                  originalProperty: 'Color',
                }
              : undefined
          }
        />
      ) : null
      break
    }
    case FillType.Gradient: {
      renderedType = gradient ? (
        <GradientAttribute
          gradient={gradient}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          opacity={opacity}
          dirtyAttributes={
            originalValue?.gradient
              ? {
                  originalValue: originalValue?.gradient as Gradient,
                  originalProperty: 'Gradient',
                }
              : undefined
          }
        />
      ) : null
      break
    }
    case FillType.Pattern: {
      renderedType = patternFillType ? (
        <Attribute>
          <Label>Image</Label>
          <Image
            opacity={opacity}
            fill={patternFillType}
            sizePercentage={patternTileScale}
          />
        </Attribute>
      ) : null
      break
    }
    default:
      renderedType = null
  }

  return (
    <AttributeList key={`Fill${i}`} data-testid="fill">
      {renderedType}
      {isBlendModeVisible(blendMode) && (
        <FullCopyAttribute
          label="Blend Mode"
          value={capitalize(blendMode)}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'blendMode',
            valueToDisplay: originalValue?.blendMode,
            originalValues: originalValue,
            labelToDisplay: 'Blend Mode',
          })}
        />
      )}
    </AttributeList>
  )
}

const Fills: React.FC<FillsProps> = ({
  fills,
  colorFormat,
  onColorFormatChange,
  layerType,
  colorVariables,
  originalValues,
}) => {
  // TODO: move this processing to the `useGetInspectorData` once https://github.com/sketch-hq/Cloud/issues/3052 is done
  const reversedFills = [...fills].reverse()
  const reversedOriginalValues = originalValues
    ? [...originalValues].reverse()
    : undefined

  const renderedFills = reversedFills
    .map((fill, index) =>
      renderFill(
        fill,
        { colorFormat, onColorFormatChange, colorVariables },
        index,
        reversedOriginalValues ? reversedOriginalValues[index] : undefined
      )
    )
    .filter(element => element !== null)

  const copySectionValue = copy(reversedFills, colorFormat, layerType)

  const dirtyHeaderAttributes = getDirtyHeaderAttributes(originalValues)

  return renderedFills.length > 0 ? (
    <>
      <Header
        copyValue={copySectionValue}
        dirtyAttributes={dirtyHeaderAttributes}>
        <SubTitle>{layerType === 'group' ? 'Tints' : 'Fills'}</SubTitle>
      </Header>
      {renderedFills}
    </>
  ) : null
}

export default Fills
